// ══════════════════════════════════════════════════════════════════
//  ED CRM - Hook de Autenticação
//  Versão: 1.0 · Abril 2026
// ══════════════════════════════════════════════════════════════════

import { useState, useEffect, useRef, useContext, createContext, type ReactNode } from 'react';
import {
  getCurrentUser, onAuthStateChange, signInWithPassword, signOut,
  getProfile, createBasicProfile, getWorkspaceMemberships, getWorkspace,
  getWorkspaceMemberRole, getTeamMembership, listAllActiveWorkspaces, listMemberWorkspaces,
  markFirstAccessDone,
} from '@app';
import type {
  AuthContextValue,
  AuthProfile,
  AuthWorkspace,
  AuthTeam,
  AuthCardOwnership,
  AuthActionResult,
  WorkspaceMembership,
  OperationalRole,
} from '@/types/auth';

// ┌──────────────────────────────────────────────────────────────┐
// │  Auth Context                                                 │
// └──────────────────────────────────────────────────────────────┘
const AuthContext = createContext<AuthContextValue | null>(null);

const withTimeout = <T,>(promise: Promise<T>, ms: number, label: string): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error(`${label} timed out after ${ms}ms`));
    }, ms);
  });

  return Promise.race([promise, timeout]).finally(() => clearTimeout(timeoutId));
};

const readAuthCache = <T,>(key: string): T | null => {
  try {
    return JSON.parse(localStorage.getItem(key) || 'null') as T | null;
  } catch {
    return null;
  }
};

const writeAuthCache = (key: string, value: unknown): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Cache is best-effort only.
  }
};

const removeAuthCache = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch {
    // Cache is best-effort only.
  }
};

const profileCacheKey = (userId: string): string => `ed_profile_${userId}`;
const workspaceCacheKey = (userId: string): string => `ed_workspace_${userId}`;
const teamCacheKey = (userId: string): string => `ed_team_${userId}`;

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AuthContextValue['user']>(null);
  const [profile, setProfile] = useState<AuthProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [workspace, setWorkspace] = useState<AuthWorkspace | null>(null);
  const [currentTeam, setCurrentTeam] = useState<AuthTeam | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

// Verificar sessão ao carregar
useEffect(() => {
  // Timeout de segurança — só dispara se NADA carregar em 15s
  timeoutRef.current = setTimeout(() => {
    console.warn('[useAuth] Timeout de segurança desativando loading');
    setLoading(false);
  }, 15000);

  const hydrateFromSession = async (sessionUser: { id: string }) => {
    const cachedProfile = readAuthCache<AuthProfile>(profileCacheKey(sessionUser.id));
    const cachedWorkspace = readAuthCache<AuthWorkspace>(workspaceCacheKey(sessionUser.id));
    const cachedTeam = readAuthCache<AuthTeam>(teamCacheKey(sessionUser.id));

    if (cachedProfile) setProfile(cachedProfile);
    if (cachedWorkspace) setWorkspace(cachedWorkspace);
    if (cachedTeam) setCurrentTeam(cachedTeam);
    if (cachedWorkspace) setLoading(false);

    await withTimeout(loadProfile(sessionUser.id), cachedWorkspace ? 4000 : 8000, 'loadProfile')
      .catch(error => console.error('[useAuth] Timeout ao carregar perfil:', error));
  };

  const initSession = async () => {
    try {
      const sessionUser = await getCurrentUser();
      if (sessionUser) {
        setUser(sessionUser);
        await hydrateFromSession(sessionUser);
      }
    } catch (error) {
      console.error('[useAuth] Erro ao restaurar sessão:', error);
    } finally {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setLoading(false);
    }
  };

  initSession();

  const unsubscribe = onAuthStateChange(async (sessionUser) => {
    if (sessionUser) {
      setUser(sessionUser);
      await hydrateFromSession(sessionUser);
    } else {
      setUser(null);
      setProfile(null);
      setWorkspace(null);
      setCurrentTeam(null);
      setLoading(false);
    }
  });

  return () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    unsubscribe();
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  // Carregar perfil do usuário
  const loadProfile = async (userId: string | undefined): Promise<void> => {
    if (!userId) return;

    try {
      let resolvedProfile: AuthProfile | null = null;

      try {
        const profileData = await getProfile(userId);
        resolvedProfile = profileData;
        setProfile(profileData);
        writeAuthCache(profileCacheKey(userId), profileData);
      } catch {
        console.warn('[useAuth] Perfil não encontrado, criando perfil básico');
        try {
          const newProfile = await createBasicProfile(userId);
          resolvedProfile = newProfile;
          setProfile(newProfile);
          if (newProfile) writeAuthCache(profileCacheKey(userId), newProfile);
        } catch (insertError) {
          console.error('[useAuth] Erro ao criar perfil básico:', insertError);
        }
      }

      try {
        await loadWorkspace(userId, resolvedProfile);
      } catch (error) {
        console.error('[useAuth] Erro ao carregar workspace (continuando):', error);
      }
    } catch (error) {
      console.error('Erro ao carregar perfil:', error);
    }
  };

  // Carregar workspace do usuário
  const loadWorkspace = async (userId: string, resolvedProfile?: AuthProfile | null): Promise<void> => {
    try {
      const memberData = await getWorkspaceMemberships(userId);
      const cachedWorkspace = readAuthCache<AuthWorkspace>(workspaceCacheKey(userId));
      const memberRoleByWorkspace = new Map((memberData || []).map(m => [m.workspace_id, m.role]));
      const isPlatformOwner = resolvedProfile?.platform_role === 'owner' || profile?.platform_role === 'owner';
      const cachedWorkspaceId = typeof cachedWorkspace?.id === 'string' ? cachedWorkspace.id : null;
      const cachedRole = cachedWorkspaceId ? memberRoleByWorkspace.get(cachedWorkspaceId) : null;
      const firstMember = memberData?.[0] || null;
      const selectedMember = cachedWorkspaceId && (cachedRole || isPlatformOwner)
        ? { workspace_id: cachedWorkspaceId, role: cachedRole || 'owner' }
        : firstMember;

      if (memberData && memberData.length > 1) {
        localStorage.setItem('available_workspaces', JSON.stringify(memberData));
      }

      if (!selectedMember && isPlatformOwner) {
        const workspaces = await listAllActiveWorkspaces();
        const fallback = workspaces[0] || null;
        if (fallback) {
          const nextWorkspace: AuthWorkspace = { ...fallback, userRole: 'owner' };
          setWorkspace(nextWorkspace);
          writeAuthCache(workspaceCacheKey(userId), nextWorkspace);
          await loadTeam(fallback.id, userId).catch((error) => {
            console.error('[useAuth] Erro ao carregar time (continuando):', error);
          });
        } else {
          setWorkspace(null);
          setCurrentTeam(null);
          removeAuthCache(workspaceCacheKey(userId));
          removeAuthCache(teamCacheKey(userId));
        }
        return;
      }

      if (!selectedMember) {
        setWorkspace(null);
        setCurrentTeam(null);
        removeAuthCache(workspaceCacheKey(userId));
        removeAuthCache(teamCacheKey(userId));
        return;
      }

      if (selectedMember) {
        const member = selectedMember;
        const workspaceData = await getWorkspace(member.workspace_id).catch(() => null);
        if (!workspaceData) {
          setWorkspace(null);
          setCurrentTeam(null);
          removeAuthCache(workspaceCacheKey(userId));
          removeAuthCache(teamCacheKey(userId));
          return;
        }

        if (workspaceData) {
          const nextWorkspace: AuthWorkspace = { ...workspaceData, userRole: member.role };
          setWorkspace(nextWorkspace);
          writeAuthCache(workspaceCacheKey(userId), nextWorkspace);
        }

        if (member.workspace_id) {
          // Carregar time padrão do workspace (não bloqueia se falhar)
          await loadTeam(member.workspace_id, userId).catch((error) => {
            console.error('[useAuth] Erro ao carregar time (continuando):', error);
          });
        } else {
          // Múltiplos workspaces - salvar lista disponível para seleção
          localStorage.setItem('available_workspaces', JSON.stringify(memberData));
        }
      }
    } catch (error) {
      console.error('Erro ao carregar workspace:', error);
    }
  };

  // Carregar time do usuário
  const loadTeam = async (workspaceId: string, userId: string): Promise<void> => {
    try {
      const teamMemberData = await getTeamMembership(userId, workspaceId);

      if (teamMemberData) {
        const nextTeam: AuthTeam = { ...teamMemberData.times, userRole: teamMemberData.role };
        setCurrentTeam(nextTeam);
        writeAuthCache(teamCacheKey(userId), nextTeam);
      } else {
        setCurrentTeam(null);
        removeAuthCache(teamCacheKey(userId));
      }
    } catch (error) {
      console.error('Erro ao carregar time:', error);
      setCurrentTeam(null);
      removeAuthCache(teamCacheKey(userId));
    }
  };

  // Login
  const login = async (email: string, password: string): Promise<AuthActionResult> => {
    try {
      const { error } = await signInWithPassword({ email, password });
      if (error) throw new Error(error);
      // Login explícito por senha = "entrou" de fato. Marca o primeiro acesso
      // (sai da lista de quem não entrou). Não bloqueia o login se falhar.
      try {
        const u = await getCurrentUser();
        if (u?.id) await markFirstAccessDone(u.id);
      } catch (e) {
        console.warn('[useAuth] falha ao marcar first_access:', e);
      }
      return { success: true };
    } catch (error) {
      console.error('Erro ao fazer login:', error);
      return { success: false, error: (error as Error).message };
    }
  };

  // Logout
  const logout = async (): Promise<void> => {
    localStorage.clear();
    sessionStorage.clear();
    setUser(null);
    setProfile(null);
    setWorkspace(null);
    setCurrentTeam(null);

    signOut().catch(error => {
      console.error('Erro silencioso no logout API:', error);
    });
  };

  // Verificar se é super admin
  const isSuperAdmin = (): boolean => {
    return profile?.platform_role === 'owner' || user?.id === '2c7a67c9-6bdb-48af-81a1-316dc86c34e9';
  };

  // Verificar se é gestor
  const isManager = (): boolean => {
    return profile?.platform_role === 'owner' || currentTeam?.userRole === 'gestor';
  };

  // Owner (flag is_owner em profiles) — mantém visão total mesmo quando SDR/Closer.
  const isOwner = (): boolean => Boolean(profile?.is_owner);

  // Gestor (operacional ou administrativo) ou owner.
  const isGestor = (): boolean => isManager();

  // Pode ver tudo no workspace ativo?
  const canViewAll = (): boolean => {
    if (isOwner() || isManager()) return true;
    const wsRole = workspace?.userRole;
    return wsRole === 'owner' || wsRole === 'admin';
  };

  const canViewGlobalFinance = (): boolean =>
    profile?.platform_role === 'owner' || profile?.platform_role === 'finance';

  // Role operacional consolidado: 'gestor' | 'closer' | 'sdr' | null.
  const userRole = (): OperationalRole | string | null => {
    if (isOwner() || isManager()) return 'gestor';
    return currentTeam?.userRole || null;
  };

  // Pode editar um card específico?
  const canEditCard = (card?: AuthCardOwnership | null): boolean => {
    if (!card) return false;
    if (canViewAll()) return true;
    return card.sdr_id === user?.id || card.closer_id === user?.id;
  };

  // Trocar workspace.
  const switchWorkspace = async (workspaceId: string): Promise<void> => {
    try {
      const userId = user?.id;
      if (!userId) throw new Error('UsuÃ¡rio nÃ£o autenticado.');

      const memberRole = await getWorkspaceMemberRole(userId, workspaceId);
      const workspaceData = await getWorkspace(workspaceId);

      let role = memberRole;
      if (!role && profile?.platform_role === 'owner') role = 'owner';
      if (!role) throw new Error('Você não é membro deste workspace.');
      if (!workspaceData) throw new Error('Workspace não encontrado.');

      const nextWorkspace: AuthWorkspace = { ...workspaceData, userRole: role };
      setWorkspace(nextWorkspace);
      writeAuthCache(workspaceCacheKey(userId), nextWorkspace);

      setCurrentTeam(null);
      removeAuthCache(teamCacheKey(userId));
      await loadTeam(workspaceId, userId).catch((error) => {
        console.error('[useAuth] Erro ao carregar time (continuando):', error);
      });
    } catch (error) {
      console.error('[useAuth] Erro ao trocar workspace:', error);
      throw error;
    }
  };

  // Listar workspaces disponíveis.
  const listWorkspaces = async (): Promise<WorkspaceMembership[]> => {
    const userId = user?.id;
    if (!userId) return [];

    try {
      // Super-admin (owner OU id chumbado, mesma regra do isSuperAdmin) vê todos
      // os workspaces ativos; membro comum vê só os que participa.
      if (isSuperAdmin()) {
        const [wsData, memberData] = await Promise.all([
          listAllActiveWorkspaces(),
          getWorkspaceMemberships(userId),
        ]);
        const roleByWs = new Map((memberData || []).map(m => [m.workspace_id, m.role]));
        return (wsData || []).map(ws => ({
          workspace_id: ws.id,
          role: roleByWs.get(ws.id) || 'owner',
          workspaces: ws,
        }));
      }

      const memberData = await listMemberWorkspaces(userId);
      return (memberData || []).map(m => ({
        workspace_id: m.workspace_id,
        role: m.role,
        workspaces: m.workspaces ?? undefined,
      }));
    } catch (error) {
      console.error('[useAuth] Erro ao listar workspaces:', error);
      return [];
    }
  };

  const value: AuthContextValue = {
    user,
    profile,
    loading,
    workspace,
    currentTeam,
    login,
    logout,
    isSuperAdmin,
    isManager,
    isGestor,
    isOwner,
    canViewAll,
    canViewGlobalFinance,
    canEditCard,
    userRole,
    refreshProfile: () => loadProfile(user?.id),
    switchWorkspace,
    listWorkspaces,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook para usar o auth context
export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};
